<script setup>
import { ref, onMounted, watch } from 'vue'
import AnalyticsService from '@/services/analytics'
import AgentService from '@/services/agent'
import MessagesTimelineChart from './charts/MessagesTimelineChart.vue'
import RoleDistributionChart from './charts/RoleDistributionChart.vue'
import { useCompanyStore } from "@/stores/company";

const companyStore = useCompanyStore();

// -- State --
const isLoading = ref(false)
const dateRange = ref(null)
const currentFilter = ref('Daily')
const currentPeriod = ref('daily') // API expects lowercase 'daily', 'monthly' etc
const activeAgentUUID = ref(null) 
const agents = ref([])

// Stats
const summaryStats = ref([
    { title: 'Total Messages', value: 0, subtext: 'All time', color: 'primary' },
    { title: 'Average Messages', value: 0, subtext: 'Per period', color: 'success' },
    { title: 'Total Periods', value: 0, subtext: '', color: 'muted' }
])

// Charts Data
const timelineData = ref(null)
const latestTimelineStat = ref({ date: '', count: 0 })
const roleDistributionData = ref(null)
const attachmentStats = ref(null)

// -- Helpers --
const setFilter = (filter) => {
    currentFilter.value = filter
    currentPeriod.value = filter.toLowerCase()
}

const toggleAgentFilter = (agentId) => {
    if (activeAgentUUID.value === agentId) {
        activeAgentUUID.value = null // Toggle off
    } else {
        activeAgentUUID.value = agentId
    }
}

// -- Fetch Data --
const fetchAgents = async () => {
    try {
        const currentCompanyId = companyStore.companyId
        if (currentCompanyId) {
            const response = await AgentService.getAgentsGrid(currentCompanyId)
            if (response && response.data) {
                agents.value = response.data.map((agent, index) => ({
                    id: agent.id,
                    name: agent.name,
                    status: agent.status,
                    color: index % 2 === 0 ? 'bg-primary' : 'bg-success'
                }))
            }
        }
    } catch (error) {
        console.error('Error fetching agents:', error)
    }
}

const fetchAllData = async () => {
    try {
        isLoading.value = true
        const currentCompanyId = companyStore.companyId
        if (!currentCompanyId) return

        const params = {
            period: currentPeriod.value
        }

        if (dateRange.value && dateRange.value[0] && dateRange.value[1]) {
            params.from_date = dateRange.value[0].toISOString()
            params.until_date = dateRange.value[1].toISOString()
            summaryStats.value[0].subtext = `${dateRange.value[0].toLocaleDateString()} - ${dateRange.value[1].toLocaleDateString()}`
        } else {
             summaryStats.value[0].subtext = 'All time'
        }
        
        // Reset data
        timelineData.value = null
        roleDistributionData.value = null
        attachmentStats.value = null
        
        if (activeAgentUUID.value) {
            params.agent_uuid = activeAgentUUID.value
        }

        // Parallel requests
        // If agent selected, use specific agent API for stats (API 3)
        // API 3 does not support 'period' param, so we filter it out but keep dates
        let agentStatsPromise = null
        if (activeAgentUUID.value) {
            const agentParams = { ...params }
            delete agentParams.period
            delete agentParams.agent_uuid
            agentStatsPromise = AnalyticsService.getAgentMessagesAnalytics(currentCompanyId, activeAgentUUID.value, agentParams)
        }

        const [avgRes, timelineRes, roleRes, attachRes, agentStatsRes] = await Promise.all([
            AnalyticsService.getMessagesAverage(currentCompanyId, params), // API 1
            AnalyticsService.getMessagesTimeline(currentCompanyId, params), // API 2
            AnalyticsService.getMessageRoleDistribution(currentCompanyId, params), // API 4
            AnalyticsService.getMessageAttachmentAnalytics(currentCompanyId, params), // API 5
            agentStatsPromise // API 3 (optional)
        ])

        // 1. Process Average/Summary Stats
        // If agent selected, prioritize agent stats
        if (activeAgentUUID.value && agentStatsRes && agentStatsRes.statistics) {
             const stats = agentStatsRes.statistics
             const input = agentStatsRes.response_metrics || {}

             summaryStats.value[0].value = stats.total_messages
             
             // API 3 doesn't give a simple "Average Messages" total, but avg_messages_per_day
             // Or we could show Avg Response Time here which is unique to agents?
             // Let's mix: Total Messages, Avg Messages/Day, Avg Response Time (if available) or Active Days
             
             summaryStats.value[1].value = Number(stats.avg_messages_per_day || 0).toFixed(1)
             summaryStats.value[1].subtext = 'Daily Average' 
             summaryStats.value[1].title = 'Avg Messages/Day'

             if (input.avg_response_time_seconds !== null) {
                 summaryStats.value[2].title = 'Avg Response Time'
                 summaryStats.value[2].value = `${Number(input.avg_response_time_seconds).toFixed(1)}s`
                 summaryStats.value[2].subtext = `Response metrics`
             } else {
                 summaryStats.value[2].title = 'Active Days'
                 summaryStats.value[2].value = stats.active_days
                 summaryStats.value[2].subtext = 'Active periods'
             }

        } else if (avgRes) {
            // Default company-wide stats
            summaryStats.value[0].value = avgRes.total_messages
            summaryStats.value[1].title = 'Average Messages'
            summaryStats.value[1].value = Number(avgRes.average_messages).toFixed(1)
            summaryStats.value[1].subtext = 'Per period'
            summaryStats.value[2].title = 'Total Periods'
            summaryStats.value[2].value = avgRes.total_periods
            summaryStats.value[2].subtext = `Periods (${currentFilter.value})`
        }

        // 2. Process Timeline
        if (timelineRes && timelineRes.data) {
             const sortedData = timelineRes.data.sort((a, b) => new Date(a.period_date) - new Date(b.period_date))
             
             const labels = sortedData.map(item => {
                 const date = new Date(item.period_date)
                 return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
             })
             
             const counts = sortedData.map(item => item.message_count)
             const userCounts = sortedData.map(item => item.user_messages)
             const assistantCounts = sortedData.map(item => item.assistant_messages)
             
             timelineData.value = {
                 labels: labels,
                 datasets: [
                    {
                      label: 'Total Messages',
                      data: counts,
                      borderColor: '#4285F4',
                      backgroundColor: 'rgba(66, 133, 244, 0.1)',
                      tension: 0.4,
                      fill: true
                    },
                     {
                      label: 'User Messages',
                      data: userCounts,
                      borderColor: '#34A853',
                      backgroundColor: 'rgba(52, 168, 83, 0.05)',
                      borderDash: [5, 5],
                      tension: 0.4,
                      hidden: true // Hidden by default to not clutter
                    },
                    {
                      label: 'Assistant Messages',
                      data: assistantCounts,
                      borderColor: '#EA4335',
                      backgroundColor: 'rgba(234, 67, 53, 0.05)',
                      borderDash: [5, 5],
                      tension: 0.4,
                      hidden: true
                    }
                 ]
             }

             if (sortedData.length > 0) {
                 const lastItem = sortedData[sortedData.length - 1]
                 latestTimelineStat.value = {
                     date: new Date(lastItem.period_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                     count: lastItem.message_count
                 }
             }
        }

        // 3. Process Role Distribution
        if (roleRes && roleRes.distribution && roleRes.distribution.length > 0) {
            const labels = roleRes.distribution.map(d => d.role.charAt(0).toUpperCase() + d.role.slice(1))
            const dataP = roleRes.distribution.map(d => d.percentage) // Or message_count
             const colors = ['#4285F4', '#34A853', '#EA4335']

            roleDistributionData.value = {
                labels: labels,
                datasets: [
                    {
                        label: 'Percentage',
                        data: dataP,
                        backgroundColor: colors.slice(0, labels.length),
                        hoverOffset: 4
                    }
                ]
            }
        }
        
        // 4. Process Attachments
        if (attachRes) {
            attachmentStats.value = attachRes
        }

    } catch (error) {
        console.error('Error fetching messages analytics:', error)
    } finally {
        isLoading.value = false
    }
}

watch([dateRange, currentFilter, activeAgentUUID], () => {
    fetchAllData()
})

onMounted(() => {
    fetchAgents()
    fetchAllData()
})
</script>

<template>
  <div class="w-full p-4 dashboard-container">
    <!-- Header -->
    <div class="flex justify-between items-start mb-4 header-row">
      <div class="flex flex-col gap-1">
        <h2 class="h4 font-bold text-white mb-0 flex items-center">
          <span class="mr-2 chart-icon">💬</span> Messages Analytics
        </h2>
        <p class="header-subtitle">Message volume by role, timeline trends, and attachment usage breakdown.</p>
      </div>
      <div class="flex gap-2">
         <div style="min-width: 250px;">
            <VueDatePicker v-model="dateRange" range :multi-calendars="{ solo: true }" placeholder="Select Date Range" :enable-time-picker="false" auto-apply class="date-picker-custom" />
         </div>
          <div class="btn-group" role="group">
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Daily' }" @click="setFilter('Daily')">Daily</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Weekly' }" @click="setFilter('Weekly')">Weekly</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Monthly' }" @click="setFilter('Monthly')">Monthly</button>
            <button type="button" class="btn btn-sm btn-filter" :class="{ active: currentFilter === 'Yearly' }" @click="setFilter('Yearly')">Yearly</button>
          </div>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="grid grid-cols-12 gap-3 mb-4">
      <div v-for="(stat, index) in summaryStats" :key="stat.title || index" class="col-span-12 md:col-span-4">
        <div class="card h-full border-0 shadow-sm rounded-3">
          <div class="card-body">
             <h6 class="card-subtitle mb-2 text-gray-500 small">{{ stat.title }}</h6>
             <h2 class="card-title font-bold mb-1">
                 <span v-if="isLoading && index === 0" class="spinner-sm" role="status" aria-hidden="true"></span>
                 <span v-else>{{ stat.value }}</span>
             </h2>
             <small :class="`text-${stat.color}`">{{ stat.subtext }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-12 gap-3 mb-4">
        <!-- Timeline Chart -->
        <div class="col-span-12 md:col-span-8">
            <div class="card border-0 shadow-sm rounded-3 h-full">
                <div class="card-body">
                    <div class="flex justify-between items-start mb-3">
                        <h5 class="card-title font-bold">Message Volume Over Time</h5>
                         <div class="timeline-stat-badge" v-if="latestTimelineStat.date">
                             <div class="timeline-stat-date">{{ latestTimelineStat.date }}</div>
                             <div class="timeline-stat-count">{{ latestTimelineStat.count }} msgs</div>
                         </div>
                    </div>
                    <div style="height: 350px;">
                        <MessagesTimelineChart v-if="timelineData" :data="timelineData" />
                        <div v-else class="h-full flex items-center justify-center text-gray-500">
                           Loading data...
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Role Distribution & Attachments -->
        <!-- Role Distribution & Attachments -->
        <div class="col-span-12 md:col-span-4">
            <div class="flex flex-col gap-3 h-full">
                <!-- Agent List Filter -->
                <div class="card border-0 shadow-sm rounded-3">
                   <div class="card-body">
                       <h5 class="card-title font-bold mb-1">Filter by Agent</h5>
                       <p class="text-gray-500 small mb-3" v-if="!activeAgentUUID">Select an agent to filter data</p>
                       <p class="text-primary small mb-3 cursor-pointer" v-else @click="activeAgentUUID = null">
                           Clear filter ✕
                       </p>

                       <div class="flex flex-col gap-2 agent-filter-list" style="max-height: 250px; overflow-y: auto;">
                           <div v-for="agent in agents" :key="agent.id"
                                class="flex items-center justify-between p-2 rounded cursor-pointer transition-all"
                                :class="activeAgentUUID === agent.id ? 'bg-primary-subtle border border-primary' : 'bg-light hover-bg-gray'"
                                @click="toggleAgentFilter(agent.id)"
                                style="cursor: pointer;">
                               <div class="flex items-center gap-3">
                                   <div class="rounded-full flex items-center justify-center text-white" :class="agent.color" style="width: 32px; height: 32px; font-weight: bold; font-size: 0.8rem;">
                                       {{ agent.name.charAt(0).toUpperCase() }}
                                   </div>
                                   <div class="flex flex-col">
                                       <span class="font-bold text-dark small">{{ agent.name }}</span>
                                   </div>
                               </div>
                               <span class="badge bg-white text-secondary border rounded-full px-2 small" v-if="activeAgentUUID === agent.id">Active</span>
                           </div>
                       </div>
                   </div>
               </div>

                <!-- Role Distribution -->
                <div class="card border-0 shadow-sm rounded-3 grow">
                    <div class="card-body">
                         <h5 class="card-title font-bold mb-3">Role Distribution</h5>
                         <div style="height: 200px;">
                             <RoleDistributionChart v-if="roleDistributionData" :data="roleDistributionData" centerLabel="Roles" />
                             <div v-else class="h-full flex items-center justify-center text-gray-500">
                                {{ isLoading ? 'Loading...' : 'No data available' }}
                             </div>
                         </div>
                    </div>
                </div>

                <!-- Attachment Stats -->
                <div class="card border-0 shadow-sm rounded-3">
                    <div class="card-body">
                        <h5 class="card-title font-bold mb-2">Attachments</h5>
                        <div v-if="attachmentStats && attachmentStats.type_statistics" class="flex flex-col gap-2">
                             <div v-for="typeStat in attachmentStats.type_statistics" :key="typeStat.type" class="flex items-center justify-between p-2 bg-light rounded">
                                 <span class="text-capitalize font-bold">{{ typeStat.type }}</span>
                                 <div class="flex items-center">
                                     <span class="badge bg-secondary mr-2">{{ typeStat.message_count }} msgs</span>
                                     <small class="text-gray-500">{{ typeStat.total_items }} items</small>
                                 </div>
                             </div>
                             <div v-if="attachmentStats.type_statistics.length === 0" class="text-gray-500 small">
                                 No attachments found in this period.
                             </div>
                        </div>
                         <div v-else class="text-gray-500 small">Checking attachments...</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  </div>
</template>

<style scoped>
.dashboard-container {
    background-color: #f8f9fa;
    min-height: 100vh;
}

.header-subtitle { font-size: 12.5px; color: rgba(255,255,255,0.55); margin: 0; line-height: 1.4; }

.header-row {
    background-color: #1a1f36;
    padding: 1.5rem;
    border-radius: 8px;
    margin-left: -12px;
    margin-right: -12px;
    margin-top: -12px;
}

.chart-icon {
    font-size: 1.5rem;
}

.btn-filter {
    background-color: #2b324a;
    color: #fff;
    border: none;
    padding: 6px 16px;
    font-size: 0.85rem;
}

.btn-filter:hover {
    background-color: #374151;
    color: #fff;
}

.btn-filter.active {
    background-color: #4285F4;
    color: #fff;
}

.btn-group {
    background-color: #2b324a;
    border-radius: 20px;
    overflow: hidden;
    padding: 2px;
}

.btn-group .btn {
    border-radius: 18px !important;
    margin: 0 2px;
}

.text-primary { color: #4285F4 !important; }
.text-success { color: #34A853 !important; }
.text-gray-500 { color: #6c757d !important; }

:deep(.date-picker-custom .dp__input:placeholder-shown) {
    text-indent: 20px !important;
}

:deep(.date-picker-custom .dp__input_icons) {
    padding: 0 !important;
}

.bg-primary-subtle {
    background-color: #e8f0fe;
}

.hover-bg-gray:hover {
    background-color: #e9ecef !important;
}

.transition-all {
    transition: all 0.2s ease;
}

.card-body {
  padding: 0 !important;
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .dashboard-container {
  background-color: transparent;
}

[data-theme="dark"] .card {
  border-color: #2d3f5a;
}

[data-theme="dark"] .card-title {
  color: #e8edf5;
}

[data-theme="dark"] .card-subtitle,
[data-theme="dark"] .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .text-gray-500 {
  color: #9aadc4 !important;
}

[data-theme="dark"] .text-primary {
  color: #4e7fff !important;
}

[data-theme="dark"] .text-success {
  color: #34d399 !important;
}

/* Agent filter rows */
[data-theme="dark"] .bg-light {
  background-color: #28364e !important;
}

[data-theme="dark"] .text-dark {
  color: #e8edf5 !important;
}

[data-theme="dark"] .hover-bg-gray:hover {
  background-color: #3d5070 !important;
}

[data-theme="dark"] .bg-primary-subtle {
  background-color: rgba(78, 127, 255, 0.15) !important;
}

[data-theme="dark"] .border-primary {
  border-color: #4e7fff !important;
}

/* Active agent badge */
[data-theme="dark"] .badge.bg-white {
  background-color: #3d5070 !important;
  color: #9aadc4 !important;
  border-color: #4e7fff !important;
}

[data-theme="dark"] .text-secondary {
  color: #9aadc4 !important;
}

/* Attachment type badges */
[data-theme="dark"] .badge.bg-secondary {
  background-color: #3d5070 !important;
  color: #e8edf5 !important;
}

/* Timeline latest-stat badge */
.timeline-stat-badge {
  display: inline-block;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  text-align: end;
  background-color: #1f2937;
  border: 1px solid transparent;
  color: #fff;
}

.timeline-stat-date {
  font-size: 0.75rem;
  line-height: 1.1;
  color: rgba(255, 255, 255, 0.65);
}

.timeline-stat-count {
  font-size: 0.875rem;
  font-weight: 700;
  color: #fff;
  margin-top: 2px;
}

[data-theme="dark"] .timeline-stat-badge {
  background-color: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .timeline-stat-date {
  color: #9aadc4;
}

[data-theme="dark"] .timeline-stat-count {
  color: #e8edf5;
}

[data-theme="dark"] .text-capitalize {
  color: #e8edf5;
}

/* Filter-by-Agent scrollable list */
.agent-filter-list {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.agent-filter-list::-webkit-scrollbar {
  width: 6px;
}

.agent-filter-list::-webkit-scrollbar-track {
  background: transparent;
}

.agent-filter-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.agent-filter-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

[data-theme="dark"] .agent-filter-list {
  scrollbar-color: #3d5070 transparent;
}

[data-theme="dark"] .agent-filter-list::-webkit-scrollbar-thumb {
  background: #3d5070;
}

[data-theme="dark"] .agent-filter-list::-webkit-scrollbar-thumb:hover {
  background: #4e6488;
}
</style>
